import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { usePropertyField, useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '@vibesites/theme-rise/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-rise/hooks/useRevealStyles';

// Default pet photo for the optional image below the stat tiles. Pulled from
// Pexels (photo ID 1975989 — "A woman is holding a dog in her arms in a
// park") — the same shot used on the Weidner reference pet-policy site
// (35degreesapartments.com/pet-policy), routed through the canonical Pexels
// CDN URL rather than the reference site's account-scoped Duda CDN URL so
// the fallback stays stable across deployments. Authors can override per-
// site via the `petpolicy.image` slot group.
const DEFAULT_PET_IMAGE_URL =
  'https://images.pexels.com/photos/1975989/pexels-photo-1975989.jpeg';

// ============================================================================
// Why this fork exists
// ----------------------------------------------------------------------------
// This PetPolicy mirrors `@vibesites/theme-rise`'s PetPolicy verbatim with
// four intentional Weidner-only deviations:
//
//   1. The bulleted list inside the Pet Restrictions box renders with
//      right-pointing arrow markers (▸) instead of the default disc bullets,
//      with each marker horizontally aligned to the baseline of the list item
//      text to its right.
//
//   2. The stat cards are laid out adaptively based on how many of the
//      five possible cards have values. The full 5-card layout matches
//      the Weidner reference site (35degreesapartments.com/pet-policy):
//      two long, short tiles on top (each spanning half the container
//      width, with reduced height) and three normal-height tiles below.
//      Reduced counts collapse to balanced grids (2x2 for four, a single
//      equal-width row for two or three, full-width for one) so partial
//      sets don't leave awkward gaps inside the right column on desktop.
//
//   3. A decorative pet photo renders directly below the stat tiles,
//      scoped to the right column of `.pet-layout` so it occupies ~50% of
//      the section width on desktop (full width on mobile). The photo is
//      configurable via a new `petpolicy.image` slot group; the image
//      field defaults to a shared stock pet photo (DEFAULT_PET_IMAGE_URL
//      — a Pexels-hosted version of the photo used on the Weidner
//      reference site) so sites without a property-specific pet photo
//      still ship a relevant image. Authors can override per-site;
//      clearing the image value hides the photo. Both `.pet-stats` and
//      the photo are grouped under a `.pet-right` wrapper that now owns
//      the column-order rules (mobile-first → above `.pet-content`;
//      desktop → right column of `.pet-layout`).
//
//   4. The two columns slide in from opposite sides (`.pet-content` from
//      the left, `.pet-right` from the right) when the section enters
//      the viewport. This replaces the parent theme's `reveal-fade-up`
//      on the section element with custom CSS rules keyed off the
//      `reveal--hidden` / `reveal--visible` state classes that
//      `useScrollReveal` still applies. Distance and easing match the
//      parent reveal so the effect stays subtle and on-brand.
//
// Deviation #1 (arrow bullets) is implemented in two places:
//   - The `className` on the `petpolicy.restrictions` RichTextContent below
//     drops the `[&_ul]:list-disc [&_ul]:pl-5` and `[&_li]:mb-0.5` utilities
//     so the inline <style> rules can take over without Tailwind's per-utility
//     specificity overriding them.
//   - The `.pet-restrictions ul / li / li::before` rules in the <style>
//     block render the arrow marker, position it absolutely on the left,
//     and indent the item text so the bullet and text sit on the same row.
//
// Deviation #2 (adaptive stat-tile layout) is implemented in two places:
//   - All five possible stat cards render as direct children of a single
//     `.pet-stats` grid, in a fixed visual order (Pets Per Household, Pet
//     Fee, Max Weight, Pet Deposit, Monthly Pet Rent). The `data-count`
//     attribute on `.pet-stats` carries the visible-card count so the CSS
//     can dispatch on it instead of relying on JSX-level row containers.
//   - The `.pet-stats[data-count="N"]` rules in the inline <style> block
//     pick a column layout per count. For count=5, a 6-column grid lets
//     the first two tiles span 3 cols each (the top "horizontal bars" —
//     same reduced min-height + tighter vertical padding as before) and
//     the last three span 2 cols each (the bottom row). Counts 1–4 use
//     balanced equal-width grids (1, 2, 3, or 2x2). On mobile every count
//     uses a simple 2-column grid (1 column for count=1), with the
//     trailing tile spanning both columns for odd counts so the last row
//     doesn't leave half-width whitespace.
//
// When updating this file:
//   - Diff against `@vibesites/theme-rise/components/PetPolicy.tsx`.
//   - The only intentional differences are (a) the className change and
//     `.pet-restrictions ul/li/li::before` rules for the arrow bullets,
//     (b) the single `.pet-stats` grid with `data-count` plus the
//     `.pet-stats[data-count="N"]` rules for the adaptive 1-to-5 tile
//     layout, (c) the `petpolicy.image` slot group + `<VibesitesImage>`
//     block + `.pet-image-wrap` / `.pet-image` styles for the photo
//     below the tiles, and (d) the `.pet-policy-section` className on
//     the root section plus the slide-in CSS rules that drive `.pet-
//     content` / `.pet-right` from the left and right. Everything else
//     should match the parent so upstream edits (CTA styling, layout
//     breakpoints, content schema) flow through automatically.
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Amenities CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Amenities',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Image rendered below the stat tiles. `image` defaults to the shared stock
// pet photo above so that even sites without a custom pet image still ship
// with a relevant photo. Authors can override per-site by setting `image`
// (and `alt`) on the `petpolicy.image` slot group; clearing the image value
// hides the photo entirely (see the conditional render in the component).
const petImageDef: SlotGroupDefinition = {
  label: 'Pet Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: DEFAULT_PET_IMAGE_URL,
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resident holding their dog at the community',
    },
  },
};

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const restrictions = usePropertyField('property_pet_restrictions') as string | null;
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);
  const petImage = useSlotGroup('petpolicy.image', petImageDef);

  const petDepositSublabel = useContentToken('petpolicy.pet_deposit_sublabel');
  const petFeeSublabel = useContentToken('petpolicy.pet_fee_sublabel');

  // Stat values — defaultField in the schema pulls the property value; authors can override per-site.
  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const maxWeightValue = useContentToken('petpolicy.max_weight_value');
  const petDepositValue = useContentToken('petpolicy.pet_deposit_value');
  const petFeeValue = useContentToken('petpolicy.pet_fee_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);
  const computedWelcomeText =
    catsDogsList.length > 0 ? `${catsDogsList.join(' & ')} Welcome` : 'Dogs & Cats Welcome';

  // Editable welcome text — authors can override it per-site.
  const welcomeText = useContentToken('petpolicy.welcome_text');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Show the welcome badge if either pet type is allowed by the property
  // OR if an author has explicitly set a custom welcome text.
  const showWelcomeBadge = catsDogsList.length > 0 || Boolean(welcomeText);

  const showMaxPets = hasValue(maxPetsValue);
  const showMaxWeight = hasValue(maxWeightValue);
  const showPetDeposit = hasValue(petDepositValue);
  const showPetFee = hasValue(petFeeValue);
  const showMonthlyRent = hasValue(monthlyRentValue);
  // Count of visible stat tiles. Surfaced to CSS via the `data-count`
  // attribute on `.pet-stats` so the grid can pick a column layout that
  // matches the actual content — see the `.pet-stats[data-count="N"]`
  // rules in the inline <style> block below.
  const statCount =
    Number(showMaxPets) +
    Number(showPetFee) +
    Number(showMaxWeight) +
    Number(showPetDeposit) +
    Number(showMonthlyRent);
  const showStats = statCount > 0;

  const descriptionHtml = policyDescription || policyInfo || null;

  return (
    <section ref={sectionRef} className="pet-policy-section py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {/* Pets welcome badge */}
            {showWelcomeBadge && (
              <div className="flex items-center gap-3 mb-6">
                <PetsAllowedIcon size={28} color="var(--vibe-color-primary)" />
                <RichTextContent
                  path="petpolicy.welcome_text"
                  as="span"
                  className="font-heading text-lg text-black font-medium tracking-wide uppercase"
                />
              </div>
            )}

            {/* Policy description */}
            {descriptionHtml && (
              <RichTextContent
                path="petpolicy.description"
                label="Pet Policy Description"
                defaultField="property_pet_policy_description"
                as="div"
                className="font-body text-[0.95rem] leading-relaxed text-[var(--vibe-color-text)] mb-6 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
              />
            )}

            {/* Pet restrictions */}
            {hasValue(restrictions) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicy.restrictions_heading"
                  as="h3"
                  className="font-heading text-base font-medium text-[var(--vibe-color-text)] mb-3 tracking-wide uppercase"
                />
                {/* Weidner-only bullet styling: arrow markers via the inline
                    <style> block below. The Tailwind list utilities the rise
                    base uses here have been intentionally dropped so they
                    don't override the inline rules with their per-utility
                    specificity. */}
                <RichTextContent
                  path="petpolicy.restrictions"
                  label="Pet Restrictions"
                  defaultField="property_pet_restrictions"
                  as="div"
                  className="font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_a]:text-primary [&_a]:underline"
                />
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              <EditableSlotGroup groupPath="petpolicy.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  {ctaLink.label || 'View Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column wrapper: holds the stat tiles and, optionally,
              the pet photo directly beneath them. This wrapper exists so
              the photo stays scoped to the right half of `.pet-layout` on
              desktop (rather than spanning the full container width below
              the layout). On mobile, `.pet-layout` collapses to a single
              column and this wrapper stacks naturally above `.pet-content`
              via the `order` rules below.

              Weidner-only stat-tile layout: all visible cards render in a
              single `.pet-stats` grid in a fixed order (Pets Per
              Household, Pet Fee, Max Weight, Pet Deposit, Monthly Pet
              Rent). The visible-count is surfaced via `data-count` so the
              CSS can swap in a layout that fits the count — see the
              `.pet-stats[data-count="N"]` rules in the inline <style>
              block. For count=5 the first two tiles read as long,
              shorter "horizontal bars" above three standard tiles
              (matching the Weidner reference site). Smaller counts use
              balanced equal-width grids so partial sets don't leave
              awkward gaps. */}
          <div className="pet-right">
          {showStats && (
            <div className="pet-stats" data-count={statCount}>
              {showMaxPets && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_pets_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_pets_value" as="span" className="pet-stat-value">
                    {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetFee && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_fee_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_fee_value" as="span" className="pet-stat-value">
                    {petFeeValue ? formatCurrency(petFeeValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_fee_sublabel" as="span" className="pet-stat-sublabel">
                    {petFeeSublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMaxWeight && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_weight_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_weight_value" as="span" className="pet-stat-value">
                    {maxWeightValue ? `${maxWeightValue} lbs` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_deposit_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_deposit_value" as="span" className="pet-stat-value">
                    {petDepositValue ? formatCurrency(petDepositValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_deposit_sublabel" as="span" className="pet-stat-sublabel">
                    {petDepositSublabel || '(Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMonthlyRent && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.monthly_rent_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.monthly_rent_value" as="span" className="pet-stat-value">
                    {monthlyRentValue ? `${formatCurrency(monthlyRentValue)}/mo` : ''}
                  </EditableToken>
                </div>
              )}
            </div>
          )}

          {/* Optional pet photo below the tiles, scoped to the right
              column so it sits at 50% of the section width on desktop.
              The slot group ships with a stock pet photo as its
              defaultValue so sites without a custom pet image still
              display something on-brand. Authors can swap in a property-
              specific photo via the editor; clearing the image value
              hides the photo entirely. */}
          {petImage.image && (
            <EditableSlotGroup groupPath="petpolicy.image" definition={petImageDef}>
              <div className="pet-image-wrap">
                <VibesitesImage
                  url={petImage.image}
                  alt={petImage.alt || ''}
                  className="pet-image"
                  objectFit="cover"
                />
              </div>
            </EditableSlotGroup>
          )}
          </div>
        </div>
      </div>

      <style>{`
        /* Weidner-only: subtle slide-in for the two columns. The section
           gets 'reveal--hidden' on mount and 'reveal--visible' once it
           enters the viewport (driven by useScrollReveal above). The
           rules below piggyback on those state classes to shift
           .pet-content in from the left and .pet-right in from the
           right. Distance (24px) and easing match the parent theme's
           reveal-fade-up so the animation reads as native to Rise.
           Elements start in their resting position so SSG/SSR renders
           don't flash off-screen — 'reveal--hidden' is applied client-
           side before the observer reveals them. */
        .pet-policy-section .pet-content,
        .pet-policy-section .pet-right {
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .pet-policy-section.reveal--hidden .pet-content {
          opacity: 0;
          transform: translateX(-24px);
        }
        .pet-policy-section.reveal--hidden .pet-right {
          opacity: 0;
          transform: translateX(24px);
        }
        .pet-policy-section.reveal--visible .pet-content,
        .pet-policy-section.reveal--visible .pet-right {
          opacity: 1;
          transform: translateX(0);
        }
        @media (prefers-reduced-motion: reduce) {
          .pet-policy-section .pet-content,
          .pet-policy-section .pet-right,
          .pet-policy-section.reveal--hidden .pet-content,
          .pet-policy-section.reveal--hidden .pet-right {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }

        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .pet-content {
            order: 1;
          }
        }

        /* Right column wrapper. Owns the "order" rules that used to live
           on .pet-stats so both the tiles and the optional photo stack
           together on the correct side of .pet-layout (above .pet-content
           on mobile, in the right column on desktop). */
        .pet-right {
          order: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        @media (min-width: 768px) {
          .pet-right {
            order: 2;
          }
        }

        /* Adaptive stat-tile grid. All visible cards are direct children
           of .pet-stats; the data-count attribute (1–5) dispatches a
           column layout that fits the actual content rather than
           hard-coding a 2+3 split that breaks for partial sets.

           Mobile baseline: a simple 2-column grid for any count > 1, with
           the trailing tile spanning both columns for odd counts so the
           last row doesn't leave half-width whitespace. count=1 stacks
           to a single column.

           Desktop (≥768px): each count gets a layout chosen for visual
           balance — 1 / 2 / 3 / 2x2 / and the original 2+3 cadence at
           count=5. count=5 also keeps the reduced min-height + tighter
           padding on the top two tiles so they read as long, narrow
           horizontal bars over more square bottom tiles (matching the
           Weidner reference site). */
        .pet-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }
        .pet-stats[data-count="1"] {
          grid-template-columns: 1fr;
        }
        /* On mobile, fill the trailing row when the count is odd so the
           last tile doesn't sit alone at half-width. */
        .pet-stats[data-count="3"] > :last-child,
        .pet-stats[data-count="5"] > :last-child {
          grid-column: span 2;
        }

        @media (min-width: 768px) {
          .pet-stats[data-count="1"] {
            grid-template-columns: 1fr;
          }
          .pet-stats[data-count="2"] {
            grid-template-columns: repeat(2, 1fr);
          }
          .pet-stats[data-count="3"] {
            grid-template-columns: repeat(3, 1fr);
          }
          /* Reset the mobile odd-count span override on desktop so the
             3-card row stays a clean three-up. */
          .pet-stats[data-count="3"] > :last-child {
            grid-column: auto;
          }
          .pet-stats[data-count="4"] {
            grid-template-columns: repeat(2, 1fr);
          }
          /* count=5: a 6-column track lets the top two tiles span 3 cols
             each (half width) and the bottom three tiles span 2 cols each
             (third width). LCM of 2 and 3 = 6. Top tiles also get the
             reduced height + tighter padding so they read as horizontal
             bars rather than square cards. */
          .pet-stats[data-count="5"] {
            grid-template-columns: repeat(6, 1fr);
          }
          .pet-stats[data-count="5"] > :nth-child(1),
          .pet-stats[data-count="5"] > :nth-child(2) {
            grid-column: span 3;
            min-height: 64px;
            padding: 0.65rem 1rem;
          }
          .pet-stats[data-count="5"] > :nth-child(n+3) {
            grid-column: span 2;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8ea;
          background: #f7f7f8;
          border-radius: 4px;
          min-height: 100px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        /* Reserve a consistent 2-line height for every stat label so
           single-line labels (e.g. "Pet Fee", "Max Weight") and labels
           that wrap to two lines (e.g. "Pets Per Household", "Monthly
           Pet Rent" at narrower widths) occupy the same vertical space.
           Without this, the value rows below would sit at different
           baselines across adjacent tiles, breaking the horizontal
           rhythm of each sub-row. line-height is pinned explicitly so
           the 2-line min-height is deterministic across browsers. */
        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin-bottom: 0.5rem;
          line-height: 1.3;
          min-height: 2.6em;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        /* Pinned line-heights below make the reserved-space math
           deterministic: the ghost sublabel gap on cards without a
           sublabel must equal sublabel margin-top (0.15rem) + sublabel
           line height (0.75rem × 1.4 ≈ 1.05rem) ≈ 1.2rem. If either
           font-size or line-height changes, update the margin-bottom
           reserve on .pet-stat-value to match. */
        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.4;
          color: var(--vibe-color-text-muted);
          /* Reserve one sublabel-row of vertical space below the value
             so every card has the same total content height regardless
             of whether it renders a sublabel. The :has() rule below
             zeroes this out on cards that DO render a real sublabel so
             they don't double-count the space. */
          margin-bottom: 1.2rem;
        }
        .pet-stat-value:has(+ .pet-stat-sublabel) {
          margin-bottom: 0;
        }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.4;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          background: #f7f7f8;
          border-left: 3px solid var(--vibe-color-primary);
          margin-bottom: 0.5rem;
        }

        /* Weidner-only: right-arrow bullets for the Pet Restrictions list.
           - Strip the default disc and reset padding so the marker can be
             positioned manually.
           - Each <li> reserves a tight left gutter and renders the ▸ via
             ::before pinned to that gutter. The marker's font-size is sized
             close to the body text (text-sm = 0.875rem) and its line-height
             is matched to the text's first-line height (text-sm
             leading-relaxed ≈ 0.875rem × 1.625 ≈ 1.422rem). Keeping the
             marker's em-box within the same line-box as the text avoids the
             baseline drift that a much larger glyph would cause, so the
             arrow's vertical center sits on the same row as the text. */
        .pet-restrictions ul {
          list-style: none;
          padding-left: 0;
          margin-bottom: 0.5rem;
        }
        .pet-restrictions li {
          position: relative;
          padding-left: 1rem;
          margin-bottom: 0.125rem;
        }
        .pet-restrictions li::before {
          content: '▸';
          position: absolute;
          left: 0;
          top: 0;
          font-size: 0.95rem;
          line-height: 1.422rem;
          color: var(--vibe-color-primary);
        }

        .pet-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #000;
          background: transparent;
          border: 2px solid var(--vibe-color-primary);
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }
        .pet-cta:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }

        /* Pet photo below the tiles, scoped to the right column of
           .pet-layout (so it occupies ~50% of the section width on
           desktop, full width on mobile). The wrap fixes a display aspect
           ratio so portrait/landscape source photos both crop to a
           consistent block; the inner image element (rendered by
           VibesitesImage) is forced to cover the box via the rule below.
           Margin-top matches the inter-row gap inside .pet-stats so the
           photo reads as part of the same vertical rhythm as the tiles. */
        .pet-image-wrap {
          margin-top: 0.75rem;
          width: 100%;
          aspect-ratio: 3 / 2;
          overflow: hidden;
          border-radius: 4px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06);
        }
        @media (min-width: 768px) {
          .pet-image-wrap {
            margin-top: 1rem;
          }
        }
        .pet-image-wrap .pet-image,
        .pet-image-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description: 'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicy.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
    },
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      description:
        'Maximum pet weight in lbs. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_label': {
      label: 'Pet Deposit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      defaultField: 'property_pet_deposit',
      description:
        'Pet deposit amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_sublabel': {
      label: 'Pet Deposit Sublabel',
      type: 'text',
      defaultValue: '(Refundable)',
    },
    'petpolicy.pet_fee_label': {
      label: 'Pet Fee Label',
      type: 'richtext',
      defaultValue: '<p>Pet Fee</p>',
    },
    'petpolicy.pet_fee_value': {
      label: 'Pet Fee Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      description:
        'Upfront pet fee amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_fee_sublabel': {
      label: 'Pet Fee Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      description:
        'Monthly pet rent amount. Pulled from the property automatically; override here to display a custom value.',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': {
      label: 'View Amenities CTA',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/amenities',
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Amenities',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'petpolicy.image': {
      label: 'Pet Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultValue:
            'https://images.pexels.com/photos/1975989/pexels-photo-1975989.jpeg',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Resident holding their dog at the community',
        },
      },
    },
  },
};

(PetPolicy as any).contentSchema = contentSchema;

export default PetPolicy;
